import React from 'react';
import PropTypes from 'prop-types';
import memoizeOne from 'memoize-one';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { Button as ButtonBase } from '@cvent/nucleus-button';
import { disabledTypeEnum, mapDeprecatedAppearanceToUpdated } from './utils';
import mapTokensToProps from './mapTokensToProps';

const mapTokens = memoizeOne(mapTokensToProps);

const processStyle = (
  buttonTheme,
  appearance,
  variant,
  iconOnly,
  isRtl,
  isBlock,
  isPill,
  active,
  disabled,
  buttonSizeUncorrected
) => {
  const buttonSize = correctButtonSize(buttonSizeUncorrected);
  const size = buttonTheme.size[buttonSize];
  const iconSize = buttonTheme.iconOnly[buttonSize];
  const paddings = iconOnly ? iconSize : size;
  const minWidth = iconOnly ? iconSize.minWidth : size.minWidth;
  let buttonVariant = appearance;
  if (variant && variant !== '') {
    buttonVariant = `${variant}_${appearance}`;
  }
  const themeColors = buttonTheme.theme[buttonVariant];
  return {
    outlineOffset: 3,
    color: active ? themeColors.textColorActive : themeColors.textColor,
    backgroundColor: active
      ? themeColors.backgroundColorActive
      : themeColors.backgroundColor,
    borderColor: active
      ? themeColors.borderColorActive
      : themeColors.borderColor,

    // consider this in the future
    //    borderRadius: isPill ? buttonTheme.isPill[1] : buttonTheme.isPill[0],
    borderRadius: isBlock ? buttonTheme.isPill[2] : buttonTheme.isPill[0],
    ...(isPill && { borderRadius: buttonTheme.isPill[1] }),
    borderWidth: buttonTheme.border.default,
    boxShadow: `0 0 0 ${buttonTheme.border.focus}px ${themeColors.outlineColor}`,
    minWidth,
    ...(!iconOnly
      ? {
          /* eslint-disable indent */
          fontSize: size.fontSize,
          lineHeight: size.lineHeight
        }
      : /* eslint-enable indent */
        {}),
    width: isBlock ? '100%' : 'auto',
    maxWidth: isBlock ? '100%' : 'auto',
    paddingTop: paddings.insetTop,
    paddingRight: paddings.insetRight,
    paddingBottom: paddings.insetBottom,
    paddingLeft: paddings.insetLeft,
    ...buttonTheme.text,
    direction: isRtl ? 'rtl' : 'ltr',
    svg: {
      fill: active ? themeColors.textColorActive : themeColors.textColor
    },
    ...(!active
      ? {
          /* eslint-disable indent */
          '&:hover': {
            color: themeColors.textColorHover,
            backgroundColor: themeColors.backgroundColorHover,
            borderColor: themeColors.borderColorHover,
            svg: {
              fill: themeColors.textColorHover
            }
          }
        }
      : /* eslint-enable indent */
        {}),
    ...(active
      ? {
          /* eslint-disable indent */
          svg: {
            fill: themeColors.textColorHover
          }
        }
      : /* eslint-enable indent */
        {}),
    '&:active': {
      color: themeColors.textColorActive,
      backgroundColor: themeColors.backgroundColorActive,
      borderColor: themeColors.borderColorActive
    },
    '&:disabled': {
      color: themeColors.textColorDisabled,
      backgroundColor: themeColors.backgroundColorDisabled,
      borderColor: themeColors.borderColorDisabled,
      cursor: 'default',
      svg: {
        fill: themeColors.textColorDisabled
      }
    },
    // styling for 'style-only' disabled buttons
    // NOTE: this is antithetical to how we manage styles in a modern carina component, but matches the established pattern
    ...(disabled === disabledTypeEnum.STYLE_ONLY
      ? {
          color: themeColors.textColorDisabled,
          backgroundColor: themeColors.backgroundColorDisabled,
          borderColor: themeColors.borderColorDisabled,
          cursor: 'default',
          svg: {
            fill: themeColors.textColorDisabled
          },
          '&:hover, &:active': {
            color: themeColors.textColorDisabled,
            backgroundColor: themeColors.backgroundColorDisabled,
            borderColor: themeColors.borderColorDisabled,
            cursor: 'default',
            svg: {
              fill: themeColors.textColorDisabled
            },
            boxShadow: 'none'
          }
        }
      : {})
  };
};

const processIconStyle = (mappedTheme, appearance, iconOnly) => {
  const themeColors = mappedTheme.theme[appearance];
  return {
    color: themeColors.textColor,
    ...(iconOnly ? { size: 'l' } : {})
  };
};

/**
 * Opinionated Button element for use in Cvent-branded interfaces. Requires
 * at least one of `icon` or `text` and usually has an `onClick` or `onPress` event handler.
 */
export default class Button extends React.Component {
  static propTypes = {
    /** Screen reader label text (e.g. aria-label) */
    accessibilityLabel: PropTypes.string,
    /** Hidden from screen reader (e.g. aria-hidden) */
    accessible: PropTypes.bool,
    /** Add active state styles */
    active: PropTypes.bool,
    /** Visual importance/prominence */
    appearance: PropTypes.oneOf([
      'filled',
      'lined',
      'ghost',
      'primary',
      'secondary',
      'tertiary'
    ]),
    /** Disables the link and changes the style appropriately. Providing the string "style-only" will allow interactions, including linking and pointer events. */
    disabled: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['style-only'])
    ]),
    /** reference to be applied to button element */
    forwardedRef: PropTypes.object,
    /** Glyph to render aside content */
    icon: PropTypes.func,
    /** Display position relative to content (RTL-aware) */
    iconAlign: PropTypes.oneOf(['start', 'end']),
    /** Make the button full width and responsive */
    isBlock: PropTypes.bool,
    /** Fully round the corners */
    isPill: PropTypes.bool,
    /** Right to left language preference */
    isRtl: PropTypes.bool,
    /** onClick function. Marking button as 'disabled' or 'disabled='style-only' will suppress this function */
    onClick: PropTypes.func,
    /** Available size variants */
    size: PropTypes.oneOf(['s', 'm', 'l']),
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    /** Text to display inside the button */
    text: PropTypes.string,
    /** variant is for use alongside appearance */
    variant: PropTypes.string
  };

  static defaultProps = {
    variant: '',
    appearance: 'lined',
    size: 'm',
    iconAlign: 'start',
    isPill: false,
    disabled: false,
    isRtl: false,
    accessible: true,
    active: false
  };

  mapAppearance = memoizeOne(mapDeprecatedAppearanceToUpdated);

  render() {
    const {
      size,
      isPill,
      text,
      appearance: initialAppearance,
      variant,
      icon,
      iconAlign,
      isBlock,
      isRtl,
      active,
      disabled: disabledProp,
      onClick,
      ...rest
    } = this.props;
    const iconOnly = this.props.icon && !this.props.text;
    const { iconBefore, iconAfter } = iconAlignment(this.props);
    const appearance = this.mapAppearance(initialAppearance);

    return (
      <ThemeConsumer>
        {theme => (
          <ButtonBase
            {...rest}
            onClick={!disabledProp ? onClick : undefined}
            style={processStyle(
              mapTokens({ theme, appearance, variant }),
              appearance.toLowerCase(),
              variant,
              iconOnly,
              isRtl,
              isBlock,
              isPill,
              active,
              disabledProp,
              size.toLowerCase()
            )}
            forwardedRef={this.props.forwardedRef}
            iconBefore={iconBefore}
            iconAfter={iconAfter}
            iconStyle={processIconStyle(
              mapTokens({ theme }),
              appearance.toLowerCase(),
              iconOnly
            )}
            isRtl={isRtl}
            disabled={disabledProp === true}
            aria-disabled={
              disabledProp === disabledTypeEnum.STYLE_ONLY || undefined
            }
          >
            {text}
          </ButtonBase>
        )}
      </ThemeConsumer>
    );
  }
}

function iconAlignment({ icon, iconAlign }) {
  const alignment = iconAlign.toLowerCase();
  return {
    iconBefore: icon && alignment === 'start' ? icon : undefined,
    iconAfter: icon && alignment === 'end' ? icon : undefined
  };
}

/* corrects deprecated api, can be removed in future release */
function correctButtonSize(size) {
  switch (size) {
    case 'medium':
      return 'm';
    case 'small':
      return 's';
    case 'large':
      return 'l';
    default:
      return size;
  }
}
